import { useEffect, useState } from "react";
import { collection, onSnapshot, orderBy, query } from "firebase/firestore";
import { db } from "@/lib/firebase";
import { useAuth } from "@/contexts/AuthContext";
import { useLanguage } from "@/contexts/LanguageContext";
import { FileText, Download, FolderOpen } from "lucide-react";
import { Button } from "@/components/ui/button";

interface DocRow {
  id: string;
  name?: string;
  type?: string;
  url?: string;
  downloadUrl?: string;
  uploadedAt?: any;
}

export function BuyerDocumentsList() {
  const { user } = useAuth();
  const { language } = useLanguage();
  const [docs, setDocs] = useState<DocRow[]>([]);
  const [loading, setLoading] = useState(true);

  const t = language === "fr"
    ? { name: "Nom", type: "Type", date: "Téléversé le", action: "Action", download: "Télécharger", empty: "Aucun document" }
    : { name: "Name", type: "Type", date: "Uploaded", action: "Action", download: "Download", empty: "No documents yet" };

  useEffect(() => {
    if (!user?.id) return;
    const q = query(collection(db, "users", user.id, "documents"), orderBy("uploadedAt", "desc"));
    const unsub = onSnapshot(
      q,
      (snap) => {
        setDocs(snap.docs.map((d) => ({ id: d.id, ...(d.data() as any) })));
        setLoading(false);
      },
      (err) => {
        console.error("[BuyerDocumentsList] snapshot error", err);
        setLoading(false);
      }
    );
    return () => unsub();
  }, [user?.id]);

  if (loading) {
    return <p className="text-sm text-muted-foreground py-6 text-center">…</p>;
  }

  if (docs.length === 0) {
    return (
      <div className="text-center py-12">
        <FolderOpen className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
        <p className="text-muted-foreground">{t.empty}</p>
      </div>
    );
  }

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-left text-sm">
        <thead>
          <tr className="border-b">
            <th className="py-2 pr-4 font-medium text-muted-foreground">{t.name}</th>
            <th className="py-2 pr-4 font-medium text-muted-foreground">{t.type}</th>
            <th className="py-2 pr-4 font-medium text-muted-foreground">{t.date}</th>
            <th className="py-2 pr-4 font-medium text-muted-foreground">{t.action}</th>
          </tr>
        </thead>
        <tbody>
          {docs.map((d) => {
            const url = d.url || d.downloadUrl;
            const date = d.uploadedAt?.toDate ? d.uploadedAt.toDate() : d.uploadedAt;
            const dateStr = date instanceof Date ? date.toLocaleDateString(language === "fr" ? "fr-CA" : "en-US") : "—";
            return (
              <tr key={d.id} className="border-b hover:bg-muted/30">
                <td className="py-2 pr-4 flex items-center gap-2">
                  <FileText className="h-4 w-4 text-muted-foreground" />
                  {d.name || d.id}
                </td>
                <td className="py-2 pr-4">{d.type || "—"}</td>
                <td className="py-2 pr-4">{dateStr}</td>
                <td className="py-2 pr-4">
                  {url ? (
                    <Button asChild variant="ghost" size="sm">
                      <a href={url} target="_blank" rel="noreferrer">
                        <Download className="h-4 w-4 mr-1" />
                        {t.download}
                      </a>
                    </Button>
                  ) : (
                    "—"
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
